import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authApi, settingsApi } from '../lib/api';
import { toast } from 'sonner';
import { IdleWarningDialog } from '../components/common/IdleWarningDialog';

const AuthContext = createContext(null);

const SESSION_TIMEOUT_CACHE_KEY = 'bgpr_session_timeout_min';
// Show the countdown dialog this many seconds before the auto-logout fires
const WARNING_SECONDS = 60;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [idleTimeoutMin, setIdleTimeoutMin] = useState(
        parseInt(sessionStorage.getItem(SESSION_TIMEOUT_CACHE_KEY) || '0', 10)
    );
    const [warningOpen, setWarningOpen] = useState(false);
    const [remainingSec, setRemainingSec] = useState(WARNING_SECONDS);
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const tickerRef = useRef(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = sessionStorage.getItem('bgpr_token') || localStorage.getItem('bgpr_token');
        if (token) {
            try {
                const response = await authApi.getMe();
                setUser(response.data);
                setIsAuthenticated(true);
            } catch (error) {
                localStorage.removeItem('bgpr_token');
                localStorage.removeItem('bgpr_user');
                setUser(null);
                setIsAuthenticated(false);
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        // Vertrauter-Computer-Token aus letztem 2FA-Login mitschicken;
        // passt er noch, überspringt das Backend die OTP-Challenge.
        const deviceToken = localStorage.getItem('bgpr_device_token') || undefined;
        const response = await authApi.login({ email, password, device_token: deviceToken });
        // Pflicht-2FA-Setup (neu angelegter Admin)?
        if (response.data?.requires_2fa_setup) {
            return {
                requires_2fa_setup: true,
                setup_id: response.data.setup_id,
                user_email: response.data.user_email,
                user_name: response.data.user_name,
            };
        }
        // 2FA-Challenge?
        if (response.data?.requires_2fa) {
            return {
                requires_2fa: true,
                challenge_id: response.data.challenge_id,
                method: response.data.method,
                methods: response.data.methods || [],
            };
        }
        const { token, user: userData } = response.data;
        localStorage.setItem('bgpr_token', token);
        localStorage.setItem('bgpr_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const completeForcedSetup = async (token, userData) => {
        localStorage.setItem('bgpr_token', token);
        localStorage.setItem('bgpr_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const verify2FA = async (challengeId, code, rememberDevice = false) => {
        const { twoFAApi } = await import('../lib/api');
        const label = (() => {
            try {
                const ua = navigator.userAgent || '';
                const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)/);
                const os = ua.includes('Win') ? 'Windows' : ua.includes('Mac') ? 'macOS' : ua.includes('Linux') ? 'Linux' : ua.includes('Android') ? 'Android' : ua.includes('iPhone') || ua.includes('iPad') ? 'iOS' : 'Gerät';
                return `${m ? m[1] : 'Browser'} auf ${os}`;
            } catch { return 'Browser'; }
        })();
        const response = await twoFAApi.verify(challengeId, code, rememberDevice, label);
        const { token, user: userData, device_token } = response.data;
        localStorage.setItem('bgpr_token', token);
        localStorage.setItem('bgpr_user', JSON.stringify(userData));
        if (device_token) {
            // Token nur speichern, wenn der User aktiv "Merken" gewählt hat
            // und das Backend uns einen Token zurückgegeben hat
            localStorage.setItem('bgpr_device_token', device_token);
        }
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const logout = useCallback((reason) => {
        // Rolle MERKEN bevor wir den Zustand löschen — danach kennen wir sie nicht mehr.
        let currentRole = null;
        try {
            const stored = localStorage.getItem('bgpr_user') || sessionStorage.getItem('bgpr_user');
            if (stored) currentRole = JSON.parse(stored)?.role || null;
        } catch { /* */ }

        sessionStorage.removeItem('bgpr_token');
        sessionStorage.removeItem('bgpr_user');
        sessionStorage.removeItem('bgpr_impersonating');
        localStorage.removeItem('bgpr_token');
        localStorage.removeItem('bgpr_user');
        setUser(null);
        setIsAuthenticated(false);
        setWarningOpen(false);
        if (reason === 'idle') {
            toast.info('Sie wurden wegen Inaktivität automatisch abgemeldet.');
        }

        // Auto-Logouts (Idle-Timer / Idle-Dialog) navigieren rollenspezifisch:
        //   - Admin/Super-Admin → /login (gewohnter Einstieg)
        //   - Mitglieder → / (öffentliche Akademie-Startseite)
        // Manuelle Logouts aus Komponenten setzen reason=undefined und navigieren selbst.
        if (reason === 'idle' || reason === 'manual') {
            const target = (currentRole === 'admin' || currentRole === 'super_admin') ? '/login' : '/';
            // window.location.assign sorgt für sauberen State-Reset (axios-Interzeptor, Toasts etc.)
            window.location.assign(target);
        }
    }, []);

    const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin';
    const isSuperAdmin = () => user?.role === 'super_admin';

    useEffect(() => {
        if (!isAuthenticated) return;
        settingsApi.getSession()
            .then((r) => {
                const m = parseInt(r.data.idle_timeout_minutes, 10) || 0;
                setIdleTimeoutMin(m);
                sessionStorage.setItem(SESSION_TIMEOUT_CACHE_KEY, String(m));
            })
            .catch(() => {});
    }, [isAuthenticated]);

    // Idle-timer with pre-logout warning countdown
    useEffect(() => {
        if (!isAuthenticated || !idleTimeoutMin || idleTimeoutMin <= 0) return;
        const totalMs = idleTimeoutMin * 60 * 1000;
        const warningMs = Math.max(0, totalMs - WARNING_SECONDS * 1000);

        const clearAll = () => {
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
            if (tickerRef.current) clearInterval(tickerRef.current);
            warningTimerRef.current = null;
            logoutTimerRef.current = null;
            tickerRef.current = null;
        };

        const startWarning = () => {
            setWarningOpen(true);
            setRemainingSec(WARNING_SECONDS);
            const startedAt = Date.now();
            tickerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startedAt) / 1000);
                setRemainingSec(Math.max(0, WARNING_SECONDS - elapsed));
            }, 1000);
            logoutTimerRef.current = setTimeout(() => {
                clearAll();
                logout('idle');
            }, WARNING_SECONDS * 1000);
        };

        const reset = () => {
            if (warningOpen) return; // activity during warning must go through dialog buttons
            clearAll();
            warningTimerRef.current = setTimeout(startWarning, warningMs);
        };

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearAll();
            events.forEach((e) => window.removeEventListener(e, reset));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, idleTimeoutMin, logout]);

    const handleStay = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (tickerRef.current) clearInterval(tickerRef.current);
        setWarningOpen(false);
        // Trigger a synthetic reset so the idle timer restarts
        window.dispatchEvent(new Event('focus'));
    }, []);

    const handleLogoutNow = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (tickerRef.current) clearInterval(tickerRef.current);
        setWarningOpen(false);
        logout('manual');
    }, [logout]);

    return (
        <AuthContext.Provider value={{
            user, loading, isAuthenticated, login, verify2FA, completeForcedSetup, logout, isAdmin, isSuperAdmin, checkAuth, idleTimeoutMin,
        }}>
            {children}
            <IdleWarningDialog
                open={warningOpen}
                remainingSeconds={remainingSec}
                onStay={handleStay}
                onLogoutNow={handleLogoutNow}
            />
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
